import React, { useState, useEffect } from 'react';
import { trustAccountsAPI, trustTransactionsAPI, clientsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, TrendingUp, TrendingDown, Shield } from 'lucide-react';
import { toast } from 'react-toastify';

export default function TrustAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acctModal, setAcctModal] = useState(false);
  const [txnModal, setTxnModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [acctForm, setAcctForm] = useState({ client: '', account_number: '', bank_name: '', branch_code: '' });
  const [txnForm, setTxnForm] = useState({ trust_account: '', amount: '', transaction_type: 'Deposit', description: '', receipt_reference: '', transaction_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const { data } = await trustAccountsAPI.list();
      setAccounts(data.results || data);
    } finally { setLoading(false); }
  };

  const loadTransactions = async (accountId = null) => {
    const params = accountId ? { trust_account: accountId } : {};
    const { data } = await trustTransactionsAPI.list(params);
    setTransactions(data.results || data);
  };

  useEffect(() => {
    loadAccounts();
    loadTransactions();
    clientsAPI.list().then(r => setClients(r.data.results || r.data));
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await trustAccountsAPI.create(acctForm);
      toast.success('Trust account created');
      setAcctModal(false);
      loadAccounts();
    } catch { toast.error('Failed to create trust account'); }
    finally { setSaving(false); }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await trustTransactionsAPI.create(txnForm);
      toast.success(`${txnForm.transaction_type} recorded`);
      setTxnModal(false);
      loadAccounts();
      loadTransactions(selectedAccount?.id);
    } catch { toast.error('Transaction failed'); }
    finally { setSaving(false); }
  };

  const openTxnModal = (account) => {
    setSelectedAccount(account);
    setTxnForm({ ...txnForm, trust_account: account.id });
    setTxnModal(true);
    loadTransactions(account.id);
  };

  const totalBalance = accounts.reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);

  return (
    <div className="p-6">
      <PageHeader
        title="Trust Accounts (IOLTA)"
        subtitle="Manage client trust funds"
        actions={<button className="btn-primary" onClick={() => setAcctModal(true)}><Plus size={16} />New Trust Account</button>}
      />

      {/* Summary */}
      <div className="card mb-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Trust Funds Under Management</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">$ {totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            <p className="text-xs text-gray-400 mt-1">{accounts.length} trust accounts</p>
          </div>
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center">
            <Shield size={28} className="text-purple-600" />
          </div>
        </div>
      </div>

      {/* Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {loading ? <div className="col-span-3 text-center py-12 text-gray-400">Loading...</div>
        : accounts.length === 0 ? <div className="col-span-3 card text-center py-12 text-gray-400">No trust accounts. Create one for a client.</div>
        : accounts.map(a => (
          <div key={a.id} className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => openTxnModal(a)}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Shield size={18} className="text-purple-600" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Balance</p>
                <p className="text-xl font-bold text-gray-900">${parseFloat(a.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <p className="font-semibold text-gray-900">{a.client_name}</p>
            <p className="text-xs text-gray-500">{a.bank_name}</p>
            <p className="text-xs text-gray-400">Acct: {a.account_number}</p>
            {a.last_reconciliation_date && <p className="text-xs text-gray-400 mt-1">Last reconciled: {a.last_reconciliation_date}</p>}
            <div className="flex gap-2 mt-3">
              <button className="flex-1 flex items-center justify-center gap-1.5 text-xs text-green-700 bg-green-50 hover:bg-green-100 rounded py-1.5"
                onClick={e => { e.stopPropagation(); setTxnForm({ ...txnForm, trust_account: a.id, transaction_type: 'Deposit' }); setSelectedAccount(a); setTxnModal(true); }}>
                <TrendingUp size={12} /> Deposit
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 text-xs text-red-700 bg-red-50 hover:bg-red-100 rounded py-1.5"
                onClick={e => { e.stopPropagation(); setTxnForm({ ...txnForm, trust_account: a.id, transaction_type: 'Withdrawal' }); setSelectedAccount(a); setTxnModal(true); }}>
                <TrendingDown size={12} /> Withdrawal
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Recent Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Date', 'Client', 'Type', 'Description', 'Reference', 'Amount'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">No transactions yet</td></tr>
              ) : transactions.slice(0, 20).map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{t.transaction_date}</td>
                  <td className="px-4 py-3 text-gray-700">{t.client_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.transaction_type} /></td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{t.description}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{t.receipt_reference || '—'}</td>
                  <td className={`px-4 py-3 font-semibold ${t.transaction_type === 'Deposit' ? 'text-green-700' : 'text-red-700'}`}>
                    {t.transaction_type === 'Deposit' ? '+' : '-'}${parseFloat(t.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Account Modal */}
      <Modal isOpen={acctModal} onClose={() => setAcctModal(false)} title="Create Trust Account">
        <form onSubmit={handleCreateAccount} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Client *</label>
            <select className="input" required value={acctForm.client} onChange={e => setAcctForm({ ...acctForm, client: e.target.value })}>
              <option value="">Select client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Bank Name *</label>
            <input className="input" required value={acctForm.bank_name} onChange={e => setAcctForm({ ...acctForm, bank_name: e.target.value })} />
          </div>
          <div>
            <label className="label">Account Number *</label>
            <input className="input" required value={acctForm.account_number} onChange={e => setAcctForm({ ...acctForm, account_number: e.target.value })} />
          </div>
          <div>
            <label className="label">Branch Code</label>
            <input className="input" value={acctForm.branch_code} onChange={e => setAcctForm({ ...acctForm, branch_code: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setAcctModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Account'}</button>
          </div>
        </form>
      </Modal>

      {/* Transaction Modal */}
      <Modal isOpen={txnModal} onClose={() => { setTxnModal(false); setSelectedAccount(null); }} title={`${txnForm.transaction_type} — ${selectedAccount?.client_name || ''}`}>
        <form onSubmit={handleTransaction} className="space-y-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
            <span className="text-gray-600">Current Balance: </span>
            <span className="font-bold text-gray-900">${parseFloat(selectedAccount?.current_balance || 0).toFixed(2)}</span>
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input" value={txnForm.transaction_type} onChange={e => setTxnForm({ ...txnForm, transaction_type: e.target.value })}>
              <option>Deposit</option>
              <option>Withdrawal</option>
            </select>
          </div>
          <div>
            <label className="label">Amount (US$) *</label>
            <input type="number" step="0.01" min="0.01" className="input" required value={txnForm.amount} onChange={e => setTxnForm({ ...txnForm, amount: e.target.value })} />
          </div>
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required value={txnForm.transaction_date} onChange={e => setTxnForm({ ...txnForm, transaction_date: e.target.value })} />
          </div>
          <div>
            <label className="label">Receipt Reference</label>
            <input className="input" value={txnForm.receipt_reference} onChange={e => setTxnForm({ ...txnForm, receipt_reference: e.target.value })} />
          </div>
          <div>
            <label className="label">Description *</label>
            <textarea className="input" rows={2} required value={txnForm.description} onChange={e => setTxnForm({ ...txnForm, description: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setTxnModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Processing...' : `Record ${txnForm.transaction_type}`}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
